/** Quick-insert prompts for Intelligence (Zap menu). */

export type SuggestedPromptScopeKind = "member" | "team" | "business";

export interface SuggestedPromptItem {
  id: string;
  label: string;
  description: string;
  prompt: string;
  requiresThread?: boolean;
  /** Only relevant in this scope kind — disabled otherwise. */
  requiresScope?: SuggestedPromptScopeKind;
  /** Uppercase toolkit slug that must be connected — disabled otherwise. */
  requiresApp?: string;
}

export interface SuggestedPromptCategory {
  key: string;
  label: string;
  items: SuggestedPromptItem[];
}

export const SUGGESTED_PROMPT_CATEGORIES: SuggestedPromptCategory[] = [
  {
    key: "summarize",
    label: "Sammanfatta",
    items: [
      {
        id: "summarize_conversation",
        label: "Sammanfatta det vi pratat om",
        description: "Ger en kortfattad sammanfattning av konversationen",
        prompt: "Sammanfatta det vi pratat om kortfattat",
        requiresThread: true,
      },
      {
        id: "pdf_action_plan",
        label: "Skapa en handlingsplan",
        description: "Exporterar en PDF med konkreta åtgärdsförslag",
        prompt: "Skapa en handlingsplan baserat på det vi pratat om",
        requiresThread: true,
      },
      {
        id: "status_report",
        label: "Ge mig en statusrapport",
        description: "Nuläge baserat på tillgänglig data",
        prompt: "Ge mig en kortfattad statusrapport baserat på tillgänglig data",
      },
    ],
  },
  {
    key: "explore",
    label: "Utforska",
    items: [
      {
        id: "pulse_history",
        label: "Visa pulshistorik och trender",
        description: "Hämtar och analyserar pulstrender över tid",
        prompt: "Visa pulshistorik och analysera trender",
      },
      {
        id: "assessments",
        label: "Hämta och analysera testresultat",
        description: "Analyserar personlighets- och kognitiva tester",
        prompt: "Hämta och analysera tillgängliga testresultat",
      },
      {
        id: "compare_team",
        label: "Jämför med resten av teamet",
        description: "Sätter individen i kontext mot övriga teammedlemmar",
        prompt: "Jämför med resten av teamet",
        requiresScope: "member",
      },
    ],
  },
  {
    key: "act",
    label: "Agera",
    items: [
      {
        id: "book_meeting",
        label: "Boka uppföljningsmöte",
        description: "Skapar en kalenderhändelse i Google Calendar",
        prompt: "Boka ett uppföljningsmöte i Google Calendar",
        requiresApp: "GOOGLECALENDAR",
      },
      {
        id: "send_summary_email",
        label: "Skicka sammanfattning via e-post",
        description: "Skickar en sammanfattning via Gmail",
        prompt: "Skicka en sammanfattning av det vi pratat om via e-post",
        requiresApp: "GMAIL",
        requiresThread: true,
      },
      {
        id: "update_crm",
        label: "Uppdatera i HubSpot",
        description: "Loggar aktivitet eller uppdaterar deal i HubSpot",
        prompt:
          "Uppdatera relevant information i HubSpot baserat på det vi pratat om",
        requiresApp: "HUBSPOT",
      },
    ],
  },
];

export const PDF_ACTION_PROMPT =
  "Skapa en handlingsplan baserat på det vi pratat om";

export const INTELLIGENCE_SUGGESTED_PROMPTS: SuggestedPromptItem[] =
  SUGGESTED_PROMPT_CATEGORIES.flatMap((c) => c.items);

type TranslateFn = (key: string) => string;

/** Returns translated suggested prompt categories using the provided i18next `t` function. */
export function getSuggestedPromptCategories(t: TranslateFn): SuggestedPromptCategory[] {
  return SUGGESTED_PROMPT_CATEGORIES.map((category) => ({
    ...category,
    label: t(`suggestedPrompts.${category.key}.label`),
    items: category.items.map((item) => ({
      ...item,
      label: t(`suggestedPrompts.items.${item.id}.label`),
      description: t(`suggestedPrompts.items.${item.id}.description`),
      prompt: t(`suggestedPrompts.items.${item.id}.prompt`),
    })),
  }));
}
